<script lang="ts" setup>
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import type FakturacneUdaje from '@/types/FakturacneUdaje'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyMemberDataService from '@/services/CompanyMemberDataService'
import type CompanyMember from '@/types/CompanyMember'
import { onMounted, ref, computed } from 'vue';
import store from '@/store'
import OrderDataService from '@/services/OrderDataService'
import {
    MinusIcon,
    PlusIcon
} from 'lucide-vue-next'
import { Card, CardHeader, CardContent, CardFooter, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'

const currentOrder = computed(() => store.state.currentOrder);

const currentOrderAddress = ref({} as Address);
const currentCompanyAddress = ref({} as Address);
const currentUserAddress = ref({} as Address);
const currentFakturacneUdaje = ref({} as FakturacneUdaje);
const currentUser = ref({} as User);
const currentCompany = ref({} as Company);
const currentHeadquarters = ref({} as Headquarters);
const currentCompanyMembers = ref([] as CompanyMember[]);
const items = ref([] as any[]);
const tab = ref(1);
const companySubStatuses = ref([] as any);
const currentCompanyUpdateOrder = ref({} as any);
const actual_company = ref({} as any);
const updated_company = ref({} as any);
const headquartersTypes = ref([] as any);

function currentTab(tabNumber: number) {
    tab.value = tabNumber
}

async function getHeadquartersTypes() {
    HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
            headquartersTypes.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUser(id: any) {
    await UserDataService.getUserById(id)
        .then(async (response: ResponseData) => {
            currentUser.value = response.data.data
            if (currentUser.value.address_id > 0)
                await getUserAddress(currentUser.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: any) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
            getCompanyHeadquarters(currentCompany.value.headquarters_id)
            getCompanyMembers(currentCompany.value.id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyMembers(id: any) {
    await CompanyMemberDataService.getAllCompanyMembersForCompany(id)
        .then((response: ResponseData) => {
            currentCompanyMembers.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getOrderAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentOrderAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUserAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentUserAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentCompanyAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyHeadquarters(id: any) {
    await HeadquartersDataService.getHeadquartersById(id)
        .then(async (response: ResponseData) => {
            currentHeadquarters.value = response.data.data
            if (currentHeadquarters.value.address_id > 0)
                await getCompanyAddress(currentHeadquarters.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getFakturacneUdaje(id: any) {
    await UserDataService.getFakturacneUdajeById(id)
        .then(async (response: ResponseData) => {
            currentFakturacneUdaje.value = response.data.data
            if (currentFakturacneUdaje.value.address_id > 0)
                await getOrderAddress(currentFakturacneUdaje.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanySubStatuses() {
    CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
            companySubStatuses.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function updateSubStatus() {
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            toast.success('Stav žiadosti bol úspešne zmenený');
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

async function getCompanyUpdateOrder() {
    OrderDataService.getCompanyUpdateOrder(currentOrder.value.id)
        .then((response: ResponseData) => {
            currentCompanyUpdateOrder.value = response.data.data
            actual_company.value = JSON.parse(currentCompanyUpdateOrder.value.actual_company)
            updated_company.value = JSON.parse(currentCompanyUpdateOrder.value.updated_company)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function refreshData() {
    items.value = JSON.parse(currentOrder.value.items)

    await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
    await getUser(currentOrder.value.user_id)
    if (currentOrder.value.company_id !== null) {
        await getCompany(currentOrder.value.company_id)
    }
    await getCompanySubStatuses();

    if (currentOrder.value.order_type == "company_edit") {
        await getCompanyUpdateOrder();
    }

    await getHeadquartersTypes();
}

onMounted(async () => {
    await refreshData();
})
</script>

<template>
    <div class="max-w-(--breakpoint-2xl) mx-auto">
        <!-- Header Card -->
        <Card class="mb-6 shadow-xs border-gray-100 gap-4 dark:border-gray-800">
            <CardHeader class="flex flex-row items-center gap-3">
                <svg class="size-8 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Zmena firmy (živnosť)">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <CardTitle class="mb-0 text-2xl font-bold text-gray-900 dark:text-white/90">Zmena firmy (živnosť)</CardTitle>
                <Badge v-if="items.length && items[0].price" variant="ghost" color="primary" size="lg"
                    class="ml-2 text-xl font-semibold text-brand-600" title="Celková cena">
                    - {{ items[0].price }}€
                </Badge>
            </CardHeader>
            <CardContent class="flex flex-col gap-4">
                <div class="flex items-center gap-2 text-lg font-semibold text-gray-700 dark:text-gray-300">
                    <svg class="size-6 text-gray-400" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Názov firmy">
                        <path d="M16 7a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                            stroke-linejoin="round" />
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                    </svg>
                    {{ currentCompany.name }}
                </div>
                <div class="text-xl text-gray-600 dark:text-gray-300">{{ currentOrder.description }}</div>
                <div class="flex flex-col gap-2 mt-2">
<FormKit type="select" v-model="currentCompany.sub_status" name="sub_status" id="sub_status"
                    :classes="{ outer: 'flex flex-col', input: 'mt-1 h-11 w-full rounded-[10px] border border-gray-300 bg-white px-4 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90' }"
                    @input="updateSubStatus">
                    <option v-for="status in companySubStatuses" :key="status.id" :value="status.id">{{ status.description }}</option>
                </FormKit>
                </div>
            </CardContent>
        </Card>
        <!-- Tabs -->
        <div class="flex flex-row justify-center gap-2 mb-6">
            <Button @click="currentTab(1)" type="button" :variant="tab === 1 ? 'default' : 'outline'"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition justify-start', tab === 1 ? 'bg-brand-600 text-white shadow-xs' : 'bg-gray-100 text-gray-700 hover:bg-brand-50 hover:text-gray-700 border-gray-200 shadow-none dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-300 dark:border-gray-800']"
                title="Faktúrované služby">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Faktúrované služby
            </Button>
            <Button v-if="currentOrder.order_type == 'company_edit'" @click="currentTab(2)" type="button"
                :variant="tab === 2 ? 'default' : 'outline'"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition justify-start', tab === 2 ? 'bg-brand-600 text-white shadow-xs' : 'bg-gray-100 text-gray-700 hover:bg-brand-50 hover:text-gray-700 border-gray-200 shadow-none dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-300 dark:border-gray-800']"
                title="Zmeny v spoločnosti">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Zmeny v spoločnosti
            </Button>
            <Button @click="currentTab(3)" type="button" :variant="tab === 3 ? 'default' : 'outline'"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition justify-start', tab === 3 ? 'bg-brand-600 text-white shadow-xs' : 'bg-gray-100 text-gray-700 hover:bg-brand-50 hover:text-gray-700 border-gray-200 shadow-none dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-300 dark:border-gray-800']"
                title="Podnikateľské údaje">
                <svg class="size-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M16 17l-4 4m0 0l-4-4m4 4V3" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" />
                </svg>
                Podnikateľské údaje
            </Button>
        </div>
        <Card class="shadow-xs border-gray-100 p-6 gap-0 dark:border-gray-800">
            <CardContent class="flex flex-col">
                <!-- Faktúrované služby -->
                <div v-if="tab === 1">
                    <div class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-gray-50 border border-gray-100 dark:bg-white/3 dark:border-gray-800">
                        <svg class="size-6 text-brand-500" fill="none" stroke="currentColor" stroke-width="2"
                            viewBox="0 0 24 24" title="Súčet cien">
                            <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                                fill="none" />
                            <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                        </svg>
                        <span class="font-semibold text-gray-900 dark:text-white/90">Suma všetkých služieb:</span>
                        <Badge variant="ghost" color="primary" size="lg"
                            class="ml-auto text-xl font-bold text-brand-700">
                            {{items.reduce((sum, i) => sum +
                            (isNaN(parseFloat(i.price ?? '0')) ? 0 : parseFloat(i.price ?? '0')), 0).toFixed(2)}} €
                        </Badge>
                    </div>
                    <Card
                        class="shadow-xs overflow-hidden sm:rounded-md rounded-none border-transparent px-4 py-5 sm:p-6 flex flex-col gap-4">
                        <div v-for="(item, index) in items" :key="index"
                            class="transition hover:bg-brand-50 rounded-lg p-3 group border border-gray-100 dark:border-gray-800">
                            <div class="grid grid-cols-9 gap-6 items-center">
                                <div class="col-span-1 text-center self-center text-gray-400 font-semibold">#{{ index +
                                    1
                                }}</div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="description"
                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popis</label>
                                    <div>{{ item.description }}</div>
                                </div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Suma</label>
                                    <Input v-model="item.price" name="amount" id="amount" class="mt-1" />
                                </div>
                                <div class="col-span-2 sm:col-span-2">
                                    <label for="amount" class="block text-sm font-medium text-gray-700 dark:text-gray-300">DPH</label>
                                    <Input v-model="item.price_vat" name="amount" id="amount" class="mt-1" />
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
                <!--Súčasná spoločnosť-->
                <div v-if="tab === 2">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <!-- Aktuálna spoločnosť -->
                        <div>
                            <Card
                                class="shadow-xs overflow-hidden sm:rounded-md rounded-none border-transparent px-4 py-5 sm:p-6 flex flex-col gap-4">
                                <div class="flex flex-col w-full">
                                    <div class="text-2xl font-bold py-4">Aktuálna spoločnosť</div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <div class="flex flex-row gap-4 w-full">
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Meno
                                                        spoločnosti</label>
                                                    <Input v-model="actual_company.obchodne_meno" id="obchodne_meno"
                                                        name="obchodne_meno" class="mt-1" />
                                                </div>
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">IČO</label>
                                                    <Input v-model="actual_company.ico" id="ico" name="ico"
                                                        class="mt-1" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                        <!-- Zmenená spoločnosť -->
                        <div>
                            <Card
                                class="shadow-xs overflow-hidden sm:rounded-md rounded-none border-transparent px-4 py-5 sm:p-6 flex flex-col gap-4">
                                <div class="flex flex-col w-full">
                                    <div class="text-2xl font-bold py-4">Zmenená spoločnosť</div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <div class="flex flex-row gap-4">
                                                <div>
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Meno
                                                        spoločnosti</label>
                                                    <Input v-model="updated_company.obchodne_meno" id="obchodne_meno"
                                                        name="obchodne_meno" class="mt-1" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-row w-full">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Nová adresa
                                                trvalého bydliska</label>
                                            <div class="flex flex-row w-full gap-2"
                                                v-if="updated_company.new_pernament_address != undefined">
                                                <div class="flex flex-col w-full">
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                                        <Input v-model="updated_company.new_pernament_address.psc"
                                                            id="psc" name="psc" class="mt-1" />
                                                    </div>
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                                        <Input v-model="updated_company.new_pernament_address.city"
                                                            id="city" name="city" class="mt-1" />
                                                    </div>
                                                </div>
                                                <div class="flex flex-col w-full">
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                                        <Input v-model="updated_company.new_pernament_address.street"
                                                            id="street" name="street" class="mt-1" />
                                                    </div>
                                                    <div>
                                                        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popisné
                                                            číslo</label>
                                                        <Input
                                                            v-model="updated_company.new_pernament_address.street_number"
                                                            id="number" name="street_number" class="mt-1" />
                                                    </div>
                                                    <div>
                                                        <label
                                                            class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                                            číslo</label>
                                                        <Input
                                                            v-model="updated_company.new_pernament_address.street_number2"
                                                            id="number2" name="street_number2" class="mt-1" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="flex flex-col w-full"
                                                v-if="updated_company.new_bussiness_address != undefined && updated_company.new_bussiness_address.psc != null">
                                                <div v-if="updated_company.new_bussiness_address.psc != undefined">
                                                    <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Nová
                                                        adresa miesta podnikania (sídla)</label>
                                                    <div class="flex flex-row w-full gap-2">
                                                        <div class="flex flex-col w-full">
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                                                <Input
                                                                    v-model="updated_company.new_bussiness_address.psc"
                                                                    id="psc" name="psc" class="mt-1" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                                                <Input
                                                                    v-model="updated_company.new_bussiness_address.city"
                                                                    id="city" name="city" class="mt-1" />
                                                            </div>
                                                        </div>
                                                        <div class="flex flex-col w-full">
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                                                <Input
                                                                    v-model="updated_company.new_bussiness_address.street"
                                                                    id="street" name="street" class="mt-1" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popisné
                                                                    číslo</label>
                                                                <Input
                                                                    v-model="updated_company.new_bussiness_address.street_number"
                                                                    id="number" name="street_number" class="mt-1" />
                                                            </div>
                                                            <div>
                                                                <label
                                                                    class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                                                    číslo</label>
                                                                <Input
                                                                    v-model="updated_company.new_bussiness_address.street_number2"
                                                                    id="number2" name="street_number2" class="mt-1" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-else>
                                                    <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Nová
                                                        adresa miesta podnikania (sídla)</label>
                                                    <Input v-model="updated_company.new_bussiness_address"
                                                        name="druh_priestoru" id="druh_priestoru" class="mt-1" />
                                                </div>
                                                <div
                                                    v-if="updated_company.new_bussiness_address.who_is_owner != undefined">
                                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Vlastník
                                                        sídla</label>
                                                    <Input
                                                        v-model="updated_company.new_bussiness_address.who_is_owner"
                                                        id="who_is_owner" name="who_is_owner" class="mt-1" />
                                                </div>
                                            </div>
                                            <div v-if="updated_company.new_establisment_address != undefined">
                                                <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Nová adresa
                                                    prevádzkarne</label>
                                                <div class="flex flex-row w-full gap-2">
                                                    <div class="flex flex-col w-full">
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                                            <Input
                                                                v-model="updated_company.new_establisment_address.psc"
                                                                id="psc" name="psc" class="mt-1" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                                            <Input
                                                                v-model="updated_company.new_establisment_address.city"
                                                                id="city" name="city" class="mt-1" />
                                                        </div>
                                                    </div>
                                                    <div class="flex flex-col w-full">
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                                            <Input
                                                                v-model="updated_company.new_establisment_address.street"
                                                                id="street" name="street" class="mt-1" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Popisné
                                                                číslo</label>
                                                            <Input
                                                                v-model="updated_company.new_establisment_address.street_number"
                                                                id="street_number_est" name="street_number_est"
                                                                class="mt-1" />
                                                        </div>
                                                        <div>
                                                            <label
                                                                class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                                                číslo</label>
                                                            <Input
                                                                v-model="updated_company.new_establisment_address.street_number2"
                                                                id="street_number2_est" name="street_number2_est"
                                                                class="mt-1" />
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>

                                        </div>
                                    </div>

                                    <div class="flex flex-row w-full"
                                        v-if="updated_company.new_establisment_address != undefined && updated_company.new_establisment_address.which_subject_of_bussiness != undefined">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Predmety
                                                podnikania</label>
                                            <div class="flex flex-row">
                                                {{ updated_company.new_establisment_address.which_subject_of_bussiness
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-row w-full" v-if="updated_company.ine_zmeny">
                                        <div class="flex flex-col w-full">
                                            <label class="block text-2xl font-bold text-gray-700 py-4 dark:text-gray-300">Iné
                                                zmeny</label>
                                            <div class="flex flex-row">
                                                <div>
                                                    <FormKit type="textarea" v-model="updated_company.ine_zmeny"
                                                        id="ine_zmeny"
                                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>

                    </div>
                </div>
                <!--PODNIKATELSKE UDAJE-->
                <div v-if="tab === 3">
                    <div class="mt-5">
                        <Card
                            class="shadow-xs overflow-hidden rounded-none border-transparent px-4 py-5 sm:p-6 flex flex-col gap-0">
                            <div class="flex flex-row pb-4">
                                <div class="flex flex-col w-full">
                                    <label for="company_name" class="text-xl font-bold">Názov spoločnosti</label>
                                    <Input v-model="currentCompany.name" name="company_name" id="company_name"
                                        class="mt-1" />
                                </div>
                            </div>
                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col">
                                    <label for="first_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krstné
                                        meno</label>
                                    <Input v-model="currentCompanyMembers[0].first_name" name="first_name"
                                        id="first_name" class="mt-1" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="last_name"
                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Priezvisko</label>
                                    <Input v-model="currentCompanyMembers[0].last_name" name="last_name" id="last_name"
                                        class="mt-1" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="gender" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Pohlavie</label>
                                    <Input v-model="currentCompanyMembers[0].gender" name="gender" id="gender"
                                        class="mt-1" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col">
                                    <label for="title_before" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul
                                        pred
                                        menom</label>
                                    <Input v-model="currentCompanyMembers[0].title_before" name="title_before"
                                        id="title_before" class="mt-1" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="title_after" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Titul za
                                        menom</label>
                                    <Input v-model="currentCompanyMembers[0].title_after" name="title_after"
                                        id="title_after" class="mt-1" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="phone" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Telefonné
                                        číslo</label>
                                    <Input v-model="currentUser.phone" name="phone" id="phone" class="mt-1" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="dob2" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Dátum
                                        narodenia</label>
                                    <FormKit type="date" v-model="currentCompanyMembers[0].date_of_birth" name="dob"
                                        id="dob2"
                                        class="mt-1 focus:ring-brand-500 focus:border-brand-500 block w-full shadow-xs sm:text-sm border-gray-300 rounded-md dark:border-gray-700" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="rodne_cislo" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Rodné
                                        číslo</label>
                                    <Input v-model="currentCompanyMembers[0].rodne_cislo" name="rodne_cislo"
                                        id="rodne_cislo" class="mt-1" />
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="nationality"
                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Národnosť</label>
                                    <Input v-model="currentCompanyMembers[0].nationality" name="nationality"
                                        id="nationality" class="mt-1" />
                                </div>
                            </div>

                            <div class="flex flex-row pb-4 gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Mesto</label>
                                    <Input v-model="currentCompanyMembers[0].city" name="city" id="city"
                                        class="mt-1" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Ulica</label>
                                    <Input v-model="currentCompanyMembers[0].street" name="street" id="street"
                                        class="mt-1" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street_number" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Súpisné
                                        číslo</label>
                                    <Input v-model="currentCompanyMembers[0].street_number" name="street_number"
                                        id="street_number" class="mt-1" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="street_number2"
                                        class="block text-sm font-medium text-gray-700 dark:text-gray-300">Orientačné
                                        číslo</label>
                                    <Input v-model="currentCompanyMembers[0].street_number2" name="street_number2"
                                        id="street_number2" class="mt-1" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="psc" class="block text-sm font-medium text-gray-700 dark:text-gray-300">PSČ</label>
                                    <Input v-model="currentCompanyMembers[0].psc" name="psc" id="psc"
                                        class="mt-1" />
                                </div>

                                <div class="flex flex-col w-full">
                                    <label for="country" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Krajina</label>
                                    <Input v-model="currentCompanyMembers[0].country" name="country" id="country"
                                        class="mt-1" />
                                </div>
                            </div>

                            <div class="flex flex-row gap-4">
                                <div class="flex flex-col w-full">
                                    <label for="registracia" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Registrácia
                                        živnosti ku dňu?</label>
                                    <Input v-model="currentCompany.zaciatok_opravnenia" name="registracia"
                                        id="registracia" class="mt-1" />
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </CardContent>
            <CardFooter class="py-3 bg-gray-50 justify-end border-t-0 pt-0 dark:bg-white/3">
                <Button type="submit"
                    class="inline-flex justify-center py-2 px-4 border border-transparent shadow-xs text-sm font-medium rounded-md text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500">
                    Uložiť
                </Button>
            </CardFooter>
        </Card>
    </div>
</template>
